import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { OutcomeSummary, OutcomesForParty } from "../../shared/Analytics";
import { percentage } from "../../shared/percentage";
import {
  decidedClaimIdsOf,
  meanReserve,
  outstandingOf,
  percentiles,
  RESERVE_HEADS,
  ReserveHeads,
  ReserveScenario,
  reserveTotal,
  scenariosByCost,
  scenariosByHeads,
} from "../../shared/reserve";
import CenteredMessageBox from "../general/CenteredMessageBox";
import { Metric } from "../general/DataAtoms";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { SegmentedControl } from "../general/SegmentedControl";
import { Tabs } from "../general/Tabs";
import { ReserveHistory, useReserveHistory } from "./ReserveHistory";
import { ReserveSnapshot } from "../../shared/reserveHistory";

const card = { borderRadius: 3 } as const;

// an amount as shown, to the penny: a hair below nothing is nothing, not -£0.00
const cents = (value: number) => Math.round(value * 100) / 100 || 0;

// the chances the range of the ultimate cost is told at
const CHANCES = [0.5, 0.75, 0.9];

type Grouping = "heads" | "cost";

/**
 * What the claim will cost the insurer, for each of its insured: the reserve
 * its outcomes indicate, its range, and the scenarios it is made of.
 */
export const ReservePage = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "reserve.page" });
  const caseContext = useCase();
  const c = caseContext.getCase();
  const parties = caseContext.getPartyDescriptors();
  const outcomes = caseContext.getOutcomes();
  const history = useReserveHistory();
  const insured = (c.parties ?? []).filter(({ client }) => client);

  const reserveOf = (partyId: string) => {
    const party = outcomes.find((o) => o.partyId === partyId);
    return party ? (
      <PartyReserve
        outcomes={party}
        claimIds={decidedClaimIdsOf(c, partyId)}
        history={history}
      />
    ) : (
      <CenteredMessageBox message={t("noOutcomes")} />
    );
  };

  return (
    <Stack spacing={1.5} sx={{ userSelect: "none" }}>
      <Box>
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
        >
          {t("title")}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mt: 0.5, maxWidth: "76ch" }}
        >
          {t("intro")}
        </Typography>
      </Box>
      {insured.length === 0 ? (
        <CenteredMessageBox message={t("noInsured")} />
      ) : insured.length === 1 ? (
        reserveOf(insured[0].id)
      ) : (
        <Tabs
          tabs={insured.map(({ id }) => ({
            label: parties[id]?.name ?? id,
            tabComponent: reserveOf(id),
          }))}
        />
      )}
    </Stack>
  );
};

const PartyReserve = ({
  outcomes,
  claimIds,
  history,
}: {
  outcomes: OutcomesForParty<OutcomeSummary>;
  claimIds: string[];
  history: ReserveSnapshot[] | undefined;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "reserve.page" });
  const { theme } = useTheme();
  const [grouping, setGrouping] = useState<Grouping>("heads");
  const reserve = meanReserve(outcomes.outcomes);
  const paid = outcomes.reservePaid;
  // with a judgment one outcome is left: there is no range of them
  const decided = outcomes.outcomes.length === 1;
  if (!reserve || !paid)
    return (
      <CenteredMessageBox message={t("unknown", { keyPrefix: "reserve" })} />
    );

  const costs = outcomes.outcomes.map(({ probability, reserve }) => ({
    probability,
    value: reserveTotal(reserve!),
  }));
  const range = percentiles(costs, CHANCES)!;
  // the outcome most likely to happen, and what it costs
  const likeliest = costs.reduce((most, cost) =>
    cost.probability > most.probability ? cost : most,
  );
  const scenarios: ReserveScenario<unknown>[] =
    grouping === "heads"
      ? scenariosByHeads(outcomes.outcomes, claimIds)!
      : scenariosByCost(outcomes.outcomes)!;

  return (
    <Stack spacing={2.25}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
          gap: 2.25,
        }}
      >
        <Paper variant="outlined" sx={{ ...card, px: 2.5, py: 2 }}>
          <SectionLabel component="h2">{t("indicated")}</SectionLabel>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 2,
              mt: 1.75,
            }}
          >
            <Metric
              label={t("weighted")}
              value={reserveTotal(reserve)}
              caption={t("weightedCaption")}
            />
            <Metric
              label={t("likeliest")}
              value={likeliest.value}
              caption={t("likeliestCaption", {
                chance: percentage.format(likeliest.probability),
              })}
            />
            <Metric label={t("paid")} value={reserveTotal(paid)} />
            <Metric
              label={t("outstanding")}
              value={reserveTotal(outstandingOf(reserve, paid))}
            />
          </Box>
        </Paper>
        {!decided && (
          <Paper variant="outlined" sx={{ ...card, px: 2.5, py: 2 }}>
            <SectionLabel component="h2">{t("range")}</SectionLabel>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 2,
                mt: 1.75,
              }}
            >
              <Metric label={t("median")} value={range.percentiles[0]} />
              <Metric label="P75" value={range.percentiles[1]} />
              <Metric label="P90" value={range.percentiles[2]} />
              <Metric label={t("maximum")} value={range.maximum} />
            </Box>
          </Paper>
        )}
      </Box>

      {!decided && (
        <Paper variant="outlined" sx={{ ...card, overflow: "hidden" }}>
          <Stack
            direction="row"
            useFlexGap
            sx={{
              flexWrap: "wrap",
              gap: 1.5,
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              pt: 2,
              pb: 1,
            }}
          >
            <Box>
              <SectionLabel component="h2">{t("scenarios")}</SectionLabel>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: "text.secondary" }}
              >
                {t(`scenariosBy.${grouping}`)}
              </Typography>
            </Box>
            <SegmentedControl
              ariaLabel={t("grouping")}
              value={grouping}
              options={(["heads", "cost"] as const).map((value) => ({
                value,
                label: t(`groupBy.${value}`),
              }))}
              onChange={setGrouping}
            />
          </Stack>
          <ScenarioTable
            scenarios={scenarios}
            reserve={reserve}
            nameOf={(scenario) => <ScenarioName scenario={scenario} />}
            barColor={theme.palette.primary.light}
          />
        </Paper>
      )}

      <ReserveHistory
        partyId={outcomes.partyId}
        history={history}
        indicated={reserveTotal(reserve)}
      />
    </Stack>
  );
};

/** What a scenario is: how many heads of claim are lost, or the costs it spans. */
const ScenarioName = ({ scenario }: { scenario: ReserveScenario<unknown> }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "reserve.page" });
  const { currency } = useCurrency();
  const band = scenario as ReserveScenario<number> & {
    from?: number;
    to?: number;
  };
  return band.from !== undefined && band.to !== undefined ? (
    <>
      {t("band", {
        from: currency.formatCompact(band.from),
        to: currency.formatCompact(band.to),
      })}
    </>
  ) : (
    <>{t(`headsLost.${scenario.key}`)}</>
  );
};

const ScenarioTable = ({
  scenarios,
  reserve,
  nameOf,
  barColor,
}: {
  scenarios: ReserveScenario<unknown>[];
  reserve: ReserveHeads;
  nameOf: (scenario: ReserveScenario<unknown>) => ReactNode;
  barColor: string;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "reserve" });
  const { currency } = useCurrency();
  const amount = { ...TABULAR_NUMERALS, whiteSpace: "nowrap" } as const;
  const figures = (heads: ReserveHeads) =>
    RESERVE_HEADS.map((head) => (
      <TableCell key={head} align="right" sx={amount}>
        {currency.format(cents(heads[head]))}
      </TableCell>
    ));

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ "& td, & th": { px: 2 } }}>
        <TableHead>
          <TableRow>
            <TableCell>{t("page.scenario")}</TableCell>
            <TableCell sx={{ minWidth: 160 }}>{t("page.chance")}</TableCell>
            {RESERVE_HEADS.map((head) => (
              <TableCell key={head} align="right">
                {t(`heads.${head}`)}
              </TableCell>
            ))}
            <TableCell align="right">{t("page.ultimate")}</TableCell>
            <TableCell align="right">{t("page.weightedColumn")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {scenarios.map((scenario) => (
            <TableRow key={String(scenario.key)}>
              <TableCell component="th" scope="row">
                {nameOf(scenario)}
              </TableCell>
              <TableCell>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${scenario.probability * 100}%`,
                        height: "100%",
                        bgcolor: barColor,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={amount}>
                    {percentage.format(scenario.probability)}
                  </Typography>
                </Stack>
              </TableCell>
              {figures(scenario.mean)}
              <TableCell align="right" sx={{ ...amount, fontWeight: 600 }}>
                {currency.format(reserveTotal(scenario.mean))}
              </TableCell>
              <TableCell align="right" sx={amount}>
                {currency.format(reserveTotal(scenario.weighted))}
              </TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ "& td, & th": { fontWeight: 700 } }}>
            <TableCell component="th" scope="row">
              {t("indicated")}
            </TableCell>
            <TableCell />
            {figures(reserve)}
            <TableCell />
            <TableCell align="right" sx={amount}>
              {currency.format(reserveTotal(reserve))}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};
